import React, { useState, useEffect } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableRow, Paper, 
    TableHead, Button, TextField, Dialog, DialogActions, DialogContent, 
    DialogContentText, DialogTitle, Autocomplete, Grid, Box, Chip,
    IconButton, Typography, Card, CardContent, Divider, Modal
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Print as PrintIcon,
    Close as CloseIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionPartidaContable() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    // Función helper para formatear el usuario a 20 caracteres (requerido por BD)
    const formatUser = (username) => {
        if (!username) username = 'ADMIN';
        if (username.length >= 20) return username.substring(0, 20);
        return username.padEnd(20, ' ');
    };

    // Función para formatear números con separador de miles y decimales
    const formatNumber = (value) => {
        if (!value && value !== 0) return '';
        const num = parseFloat(value);
        if (isNaN(num)) return '';
        return num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Función para parsear números formateados a float
    const parseFormattedNumber = (value) => {
        if (!value) return 0;
        // Remover comas y convertir a número
        const cleanValue = value.toString().replace(/,/g, '');
        const num = parseFloat(cleanValue);
        return isNaN(num) ? 0 : num;
    };

    // Estados para catálogos
    const [cuentas, setCuentas] = useState([]);
    const [centrosCosto, setCentrosCosto] = useState([]);
    const [estados, setEstados] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [partidas, setPartidas] = useState([]);
    const [unidadesNegocio, setUnidadesNegocio] = useState([]);
    const [procesos, setProcesos] = useState([]);
    const [tiposPartida, setTiposPartida] = useState([]);
    const [unidadesDisponibles, setUnidadesDisponibles] = useState([]);
    
    // Estados de filtro
    const [filtroSucursal, setFiltroSucursal] = useState(null);
    const [filtroUnidadNegocio, setFiltroUnidadNegocio] = useState(null);
    
    // Estados del encabezado de partida
    const [fechaPartida, setFechaPartida] = useState(new Date().toISOString().split('T')[0]);
    const [concepto, setConcepto] = useState('');
    const [estado, setEstado] = useState(null);
    const [tipoPartida, setTipoPartida] = useState(null);
    const [sucursal, setSucursal] = useState(null);
    const [referenciaDocumento, setReferenciaDocumento] = useState('');
    const [observaciones, setObservaciones] = useState('');
    
    // Estados del detalle de asientos
    const [asientos, setAsientos] = useState([]);
    const [nextSecuencia, setNextSecuencia] = useState(1);
    
    // Estados de control
    const [editingPartida, setEditingPartida] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [partidaToDelete, setPartidaToDelete] = useState(null);

    // Estados de paginación y filtros de búsqueda
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [totalPartidas, setTotalPartidas] = useState(0);
    const [fechaInicio, setFechaInicio] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        return d.toISOString().split('T')[0];
    });
    const [fechaFin, setFechaFin] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    });
    const [filtroEstado, setFiltroEstado] = useState(null);
    const [filtroTipoPartida, setFiltroTipoPartida] = useState(null);

    // Estados para el modal de visualización PDF
    const [openModalPdf, setOpenModalPdf] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfNombre, setPdfNombre] = useState('');

    useEffect(() => {
        fetchCatalogos();
        fetchPartidas();
    }, []);

    // Limpiar URL del PDF al cerrar modal
    useEffect(() => {
        if (!openModalPdf && pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
        }
    }, [openModalPdf]);

    useEffect(() => {
        if (user) {
            console.log('👤 Usuario cargado:', {
                codigoUsuario: user.codigoUsuario,
                length: user.codigoUsuario?.length,
                nombreCompleto: user.nombreCompleto,
                idSucursal: user.idSucursal
            });
            
            // Cargar la sucursal del usuario automáticamente
            if (user.idSucursal && sucursales.length > 0) {
                const sucursalUsuario = sucursales.find(s => s.idSucursal === user.idSucursal);
                if (sucursalUsuario) {
                    setSucursal(sucursalUsuario);
                    setFiltroSucursal(sucursalUsuario);
                }
            }
        }
    }, [user, sucursales]);

    // Cargar unidades de negocio cuando cambia la sucursal
    useEffect(() => {
        const fetchUnidadesPorSucursal = async () => {
            if (filtroSucursal) {
                try {
                    const response = await fetch(`${server}/CtbUnidadNegocioXSucursals/BySucursal/${filtroSucursal.idSucursal}`);
                    const data = await response.json();
                    setUnidadesDisponibles(data.$values || data || []);
                    
                    // Si solo hay una unidad, preseleccionarla
                    if ((data.$values || data || []).length === 1) {
                        setFiltroUnidadNegocio((data.$values || data)[0]);
                    } else {
                        setFiltroUnidadNegocio(null);
                    }
                } catch (error) {
                    console.error('Error al cargar unidades de negocio:', error);
                    setUnidadesDisponibles([]);
                }
            } else {
                setUnidadesDisponibles([]);
                setFiltroUnidadNegocio(null);
            }
        };
        
        fetchUnidadesPorSucursal();
    }, [filtroSucursal]);

    const fetchCatalogos = async () => {
        try {
            // Cargar todos los catálogos en paralelo
            const [
                cuentasRes,
                centrosRes,
                estadosRes,
                sucursalesRes,
                unidadesRes,
                procesosRes,
                tiposPartidaRes
            ] = await Promise.all([
                fetch(`${server}/CtbPlanDeCuentas/detalle`),
                fetch(`${server}/CtbCentroCostoXUnidadNegocios`),
                fetch(`${server}/CtbEstados`),
                fetch(`${server}/MgSucursales`),
                fetch(`${server}/CtbUnidadNegocios`),
                fetch(`${server}/MgProcesoes`),
                fetch(`${server}/CtbTipoPartida`)
            ]);

            const [cuentasData, centrosData, estadosData, sucursalesData, unidadesData, procesosData, tiposPartidaData] = await Promise.all([
                cuentasRes.json(),
                centrosRes.json(),
                estadosRes.json(),
                sucursalesRes.json(),
                unidadesRes.json(),
                procesosRes.json(),
                tiposPartidaRes.json()
            ]);

            // Filtrar solo cuentas que aceptan movimientos (nivel 5)
            const cuentasMovimiento = (cuentasData.$values || []);
            setCuentas(cuentasMovimiento);
            
            const centrosArray = centrosData.$values || [];
            setCentrosCosto(centrosArray);
            
            setEstados(estadosData.$values || []);
            setSucursales(sucursalesData.$values || []);
            setUnidadesNegocio(unidadesData.$values || []);
            setProcesos(procesosData.$values || []);
            setTiposPartida(tiposPartidaData.$values || tiposPartidaData || []);

            console.log('✅ Catálogos cargados:', {
                cuentas: cuentasMovimiento.length,
                centros: centrosArray.length,
                estados: (estadosData.$values || []).length,
                sucursales: (sucursalesData.$values || []).length
            });
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los catálogos necesarios',
            });
        }
    };

    const fetchPartidas = async (pageNum = page) => {
        try {
            const body = {
                page: pageNum,
                limit: limit,
                search: searchQuery || '',
                idSucursal: filtroSucursal?.idSucursal || 0,
                idEstado: filtroEstado?.id || 0,
                idTipoPartida: filtroTipoPartida?.id || 0,
                fechaInicio: fechaInicio ? new Date(fechaInicio).toISOString() : null,
                fechaFin: fechaFin ? new Date(fechaFin).toISOString() : null
            };
            const response = await fetch(`${server}/CtbPartidasContables/Paginados`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            setPartidas(data.partidas?.$values || data.partidas || []);
            setTotalPartidas(data.total || 0);
        } catch (error) {
            console.error('Error al cargar partidas:', error);
        }
    };

    // Función de filtrado de centros de costo por sucursal y unidad de negocio
    const getCentrosCostoFiltrados = () => {
        let filtrados = centrosCosto;
        
        if (filtroSucursal) {
            filtrados = filtrados.filter(cc => cc.idSucursal === filtroSucursal.idSucursal);
        }
        
        if (filtroUnidadNegocio) {
            filtrados = filtrados.filter(cc => cc.idUnidadNegocio === filtroUnidadNegocio.id);
        }
        
        return filtrados;
    };

    const agregarAsiento = () => {
        const nuevoAsiento = {
            idAsientoContable: 0,
            secuencia: nextSecuencia,
            idCuenta: null,
            cuenta: null,
            debe: 0,
            debeStr: '',
            haber: 0,
            haberStr: '',
            concepto: '',
            idCentroCosto: null,
            centroCosto: null,
            idSucursal: filtroSucursal?.idSucursal || null,
            idUnidadNegocio: filtroUnidadNegocio?.id || null,
            isNew: true
        };
        
        setAsientos([...asientos, nuevoAsiento]);
        setNextSecuencia(nextSecuencia + 1);
    };

    const eliminarAsiento = (index) => {
        const nuevosAsientos = asientos.filter((_, i) => i !== index);
        setAsientos(nuevosAsientos);
    };

    const actualizarAsiento = (index, campo, valor) => {
        const nuevosAsientos = [...asientos];
        
        if (campo === 'cuenta') {
            nuevosAsientos[index].cuenta = valor;
            nuevosAsientos[index].idCuenta = valor?.idCuenta || null;
        } else if (campo === 'centroCosto') {
            nuevosAsientos[index].centroCosto = valor;
            nuevosAsientos[index].idCentroCosto = valor?.id || null;
        } else if (campo === 'debeStr' || campo === 'haberStr') {
            nuevosAsientos[index][campo] = valor;
        } else if (campo === 'debe' || campo === 'haber') {
            const numValor = parseFloat(valor) || 0;
            nuevosAsientos[index][campo] = numValor;
            
            // Si se ingresa un valor en Debe, limpiar Haber y viceversa
            if (campo === 'debe' && numValor > 0) {
                nuevosAsientos[index].haber = 0;
                nuevosAsientos[index].haberStr = '';
            } else if (campo === 'haber' && numValor > 0) {
                nuevosAsientos[index].debe = 0;
                nuevosAsientos[index].debeStr = '';
            }
        } else {
            nuevosAsientos[index][campo] = valor;
        }
        
        setAsientos(nuevosAsientos);
    };

    const calcularTotales = () => {
        const totalDebe = asientos.reduce((sum, a) => sum + (parseFloat(a.debe) || 0), 0);
        const totalHaber = asientos.reduce((sum, a) => sum + (parseFloat(a.haber) || 0), 0);
        const diferencia = Math.abs(totalDebe - totalHaber);
        const cuadra = diferencia < 0.01; // Tolerancia de centavos
        
        return { totalDebe, totalHaber, diferencia, cuadra };
    };

    const validarAsientos = () => {
        const errores = [];
        
        if (asientos.length === 0) {
            errores.push('Debe agregar al menos un asiento contable');
        }
        
        asientos.forEach((asiento, index) => {
            if (!asiento.idCuenta) {
                errores.push(`Asiento ${index + 1}: Debe seleccionar una cuenta contable`);
            }
            
            const debe = parseFloat(asiento.debe) || 0;
            const haber = parseFloat(asiento.haber) || 0;
            
            if (debe > 0 && haber > 0) {
                errores.push(`Asiento ${index + 1}: No puede tener valor en Debe y Haber al mismo tiempo`);
            }
            
            if (debe === 0 && haber === 0) {
                // Estos se omitirán al guardar, pero advertir al usuario
                console.warn(`Asiento ${index + 1} será omitido (sin valores en Debe/Haber)`);
            }
        });
        
        const { cuadra, diferencia } = calcularTotales();
        if (!cuadra) {
            errores.push(`Los totales no cuadran. Diferencia: $${diferencia.toFixed(2)}`);
        }
        
        return errores;
    };

    const limpiarFormulario = () => {
        setFechaPartida(new Date().toISOString().split('T')[0]);
        setConcepto('');
        setEstado(null);
        setTipoPartida(null);
        setSucursal(null);
        setReferenciaDocumento('');
        setObservaciones('');
        setAsientos([]);
        setNextSecuencia(1);
        setEditingPartida(null);
    };

    // Función registrarDocumentosAuxiliares removida - ahora se maneja desde GestionAuxiliares.jsx

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones
        if (!concepto.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'El concepto es obligatorio',
            });
            return;
        }

        const erroresValidacion = validarAsientos();
        if (erroresValidacion.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Errores de validación',
                html: '<ul style="text-align: left;">' + 
                      erroresValidacion.map(e => `<li>${e}</li>`).join('') + 
                      '</ul>',
                width: '600px'
            });
            return;
        }

        // Filtrar asientos válidos (omitir los que tienen 0 en debe y haber)
        const asientosValidos = asientos.filter(a => {
            const debe = parseFloat(a.debe) || 0;
            const haber = parseFloat(a.haber) || 0;
            return debe > 0 || haber > 0;
        });

        const { totalDebe, totalHaber } = calcularTotales();

        // Construir objeto de partida contable
        const partida = {
            idPartidaContable: editingPartida?.idPartidaContable || 0,
            numeroPartida: editingPartida?.numeroPartida || '',
            secuencialManual: editingPartida?.secuencialManual || 0,
            isAutomatica: false,
            fechaPartida: fechaPartida,
            concepto: concepto.trim(),
            idTipoPartida: tipoPartida?.id || null,
            codigoTipoPartida: '',
            totalDebe: totalDebe,
            totalHaber: totalHaber,
            idEstado: estado?.id || null,
            idSucursal: sucursal?.idSucursal || null,
            referenciaDocumento: referenciaDocumento.trim(),
            observaciones: observaciones.trim(),
        };

        // Agregar campos de auditoría
        if (editingPartida) {
            partida.ingresadoPor = editingPartida.ingresadoPor;
            partida.modificadoPor = user?.codigoUsuario || formatUser('ADMIN');
        } else {
            partida.ingresadoPor = user?.codigoUsuario || formatUser('ADMIN');
        }

        try {
            // 1. Guardar la partida contable (encabezado)
            const method = editingPartida ? 'PUT' : 'POST';
            const endpoint = editingPartida 
                ? `${server}/CtbPartidasContables/${editingPartida.idPartidaContable}` 
                : `${server}/CtbPartidasContables`;

            const responsePartida = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(partida),
            });

            if (!responsePartida.ok) {
                throw new Error(`Error al guardar partida: ${responsePartida.status}`);
            }

            // El PUT devuelve 204 No Content, el POST devuelve 201 con el objeto
            let idPartida;
            if (editingPartida) {
                // En edición, ya tenemos el ID
                idPartida = editingPartida.idPartidaContable;
            } else {
                // En creación, obtenemos el ID del response
                const savedPartida = await responsePartida.json();
                idPartida = savedPartida.idPartidaContable;
            }

            // 2. Obtener asientos existentes si estamos editando
            let asientosExistentes = [];
            if (editingPartida) {
                try {
                    const responseAsientos = await fetch(`${server}/CtbAsientosContables`);
                    const dataAsientos = await responseAsientos.json();
                    asientosExistentes = (dataAsientos.$values || [])
                        .filter(a => a.idPartidaContable === idPartida);
                } catch (error) {
                    console.warn('Error al obtener asientos existentes:', error);
                }
            }

            // 3. Guardar o actualizar los asientos contables (detalle)
            const asientosActualizados = [];
            for (let i = 0; i < asientosValidos.length; i++) {
                const asiento = asientosValidos[i];
                
                // Determinar si es un asiento existente o nuevo
                const isNewAsiento = !asiento.idAsientoContable || asiento.idAsientoContable === 0 || asiento.isNew;
                
                const asientoData = {
                    idAsientoContable: isNewAsiento ? 0 : asiento.idAsientoContable,
                    idPartidaContable: idPartida,
                    secuencia: asiento.secuencia,
                    idCuenta: asiento.idCuenta,
                    debe: parseFloat((parseFloat(asiento.debe) || 0).toFixed(2)),
                    haber: parseFloat((parseFloat(asiento.haber) || 0).toFixed(2)),
                    concepto: asiento.concepto || concepto,
                    idCentroCosto: asiento.idCentroCosto || null,
                    idSucursal: asiento.idSucursal || filtroSucursal?.idSucursal || null,
                    idUnidadNegocio: asiento.idUnidadNegocio || filtroUnidadNegocio?.id || null
                };

                // Agregar campos de auditoría
                if (isNewAsiento) {
                    asientoData.ingresadoPor = user?.codigoUsuario || formatUser('ADMIN');
                } else {
                    asientoData.ingresadoPor = asiento.ingresadoPor || formatUser('ADMIN');
                    asientoData.modificadoPor = user?.codigoUsuario || formatUser('ADMIN');
                }

                const methodAsiento = isNewAsiento ? 'POST' : 'PUT';
                const endpointAsiento = isNewAsiento 
                    ? `${server}/CtbAsientosContables`
                    : `${server}/CtbAsientosContables/${asiento.idAsientoContable}`;

                const responseAsiento = await fetch(endpointAsiento, {
                    method: methodAsiento,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(asientoData),
                });

                if (!responseAsiento.ok) {
                    throw new Error(`Error al guardar asiento ${i + 1}: ${responseAsiento.status}`);
                }

                if (!isNewAsiento) {
                    asientosActualizados.push(asiento.idAsientoContable);
                }
            }

            // 4. Eliminar asientos que ya no están en la lista (solo al editar)
            if (editingPartida && asientosExistentes.length > 0) {
                for (const asientoExistente of asientosExistentes) {
                    if (!asientosActualizados.includes(asientoExistente.idAsientoContable)) {
                        try {
                            await fetch(
                                `${server}/CtbAsientosContables/${asientoExistente.idAsientoContable}/${encodeURIComponent(formatUser(user?.codigoUsuario || 'ADMIN'))}`,
                                { method: 'DELETE' }
                            );
                        } catch (deleteError) {
                            console.warn('Error al eliminar asiento:', deleteError);
                        }
                    }
                }
            }

            // Éxito
            Swal.fire({
                icon: 'success',
                title: 'Éxito',
                text: `Partida contable ${editingPartida ? 'actualizada' : 'creada'} correctamente`,
                timer: 2000,
                showConfirmButton: false,
            });

            limpiarFormulario();
            await fetchPartidas();

        } catch (error) {
            console.error('Error en handleSubmit:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                html: `<div style="text-align: left;">
                    <p><strong>No se pudo guardar la partida contable</strong></p>
                    <p>Detalles: ${error.message || error}</p>
                </div>`,
                width: '600px'
            });
        }
    };

    const handleDelete = async () => {
        if (!partidaToDelete) return;

        try {
            // Primero eliminar los asientos asociados
            const responseAsientos = await fetch(`${server}/CtbAsientosContables`);
            const dataAsientos = await responseAsientos.json();
            const asientosAsociados = (dataAsientos.$values || [])
                .filter(a => a.idPartidaContable === partidaToDelete.idPartidaContable);
            
            for (const asiento of asientosAsociados) {
                await fetch(
                    `${server}/CtbAsientosContables/${asiento.idAsientoContable}/${encodeURIComponent(formatUser(user?.codigoUsuario || 'ADMIN'))}`,
                    { method: 'DELETE' }
                );
            }

            // Luego eliminar la partida
            const response = await fetch(
                `${server}/CtbPartidasContables/${partidaToDelete.idPartidaContable}/${encodeURIComponent(formatUser(user?.codigoUsuario || 'ADMIN'))}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Éxito',
                    text: 'Partida eliminada correctamente',
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchPartidas();
            } else {
                throw new Error(`Error ${response.status}`);
            }
        } catch (error) {
            console.error('Error en handleDelete:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar la partida contable',
            });
        } finally {
            setOpenDialog(false);
            setPartidaToDelete(null);
        }
    };

    const imprimirPartida = async (idPartidaContable) => {
        try {
            const response = await fetch(`${server}/InformesContablesLinux/asiento/${idPartidaContable}/ReportAsiento`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/pdf',
                }
            });

            if (!response.ok) {
                throw new Error(`Error al generar el PDF: ${response.status}`);
            }

            // Obtener el blob del PDF
            const blob = await response.blob();
            
            // Crear una URL temporal para el blob
            const urlBlob = window.URL.createObjectURL(blob);
            
            // Configurar y abrir modal con el PDF
            const nombreArchivo = `Asiento_${idPartidaContable}_${new Date().toISOString().split('T')[0]}.pdf`;
            setPdfUrl(urlBlob);
            setPdfNombre(nombreArchivo);
            setOpenModalPdf(true);

           

        } catch (error) {
            console.error('Error al imprimir:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo generar el PDF de la partida',
            });
        }
    };

    // Cerrar modal PDF
    const handleCloseModalPdf = () => {
        setOpenModalPdf(false);
    };

    // Descargar PDF
    const handleDownloadPdf = () => {
        if (pdfUrl && pdfNombre) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = pdfNombre;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            Swal.fire({
                icon: 'success',
                title: 'Descargado',
                text: 'El PDF se ha descargado correctamente',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    const cargarPartidaParaEditar = async (partida) => {
        setEditingPartida(partida);
        setFechaPartida(partida.fechaPartida ? partida.fechaPartida.split('T')[0] : new Date().toISOString().split('T')[0]);
        setConcepto(partida.concepto || '');
        setEstado(estados.find(e => e.id === partida.idEstado) || null);
        setTipoPartida(tiposPartida.find(tp => tp.id === partida.idTipoPartida) || null);
        setSucursal(sucursales.find(s => s.idSucursal === partida.idSucursal) || null);
        setReferenciaDocumento(partida.referenciaDocumento || '');
        setObservaciones(partida.observaciones || '');

        // Cargar asientos asociados
        try {
            const response = await fetch(`${server}/CtbAsientosContables`);
            const data = await response.json();
            const asientosPartida = (data.$values || [])
                .filter(a => a.idPartidaContable === partida.idPartidaContable)
                .sort((a, b) => (a.secuencia || 0) - (b.secuencia || 0));

            const asientosCargados = asientosPartida.map(a => ({
                idAsientoContable: a.idAsientoContable,
                secuencia: a.secuencia,
                idCuenta: a.idCuenta,
                cuenta: cuentas.find(c => c.idCuenta === a.idCuenta) || null,
                debe: a.debe || 0,
                debeStr: (a.debe || 0) ? formatNumber(a.debe) : '',
                haber: a.haber || 0,
                haberStr: (a.haber || 0) ? formatNumber(a.haber) : '',
                concepto: a.concepto || '',
                idCentroCosto: a.idCentroCosto,
                centroCosto: centrosCosto.find(c => c.id === a.idCentroCosto) || null,
                idSucursal: a.idSucursal || null,
                idUnidadNegocio: a.idUnidadNegocio || null,
                isNew: false
            }));

            setAsientos(asientosCargados);
            setNextSecuencia(asientosCargados.length + 1);
        } catch (error) {
            console.error('Error al cargar asientos:', error);
        }
    };

    const { totalDebe, totalHaber, diferencia, cuadra } = calcularTotales();

    const totalPages = Math.ceil(totalPartidas / limit) || 1;

    const handleBuscar = () => {
        setPage(1);
        fetchPartidas(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchPartidas(newPage);
    };

    return (
        <div className="right_col" role="main">
            <div className="">
                <div className="page-title">
                    <div className="title_left">
                        <h3>Gestión de Partidas Contables</h3>
                    </div>
                </div>
                <div className="clearfix"></div>
                    
                    <div className="row">
                        <div className="col-md-12 col-sm-12">
                            <div className="x_panel">
                                <div className="x_content">
                    
                                    {/* Formulario de Partida Contable */}
                                    <Paper sx={{ p: 3, mb: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {editingPartida ? 'Editar Partida Contable' : 'Nueva Partida Contable'}
                                        </Typography>
                                        <form onSubmit={handleSubmit}>
                                            {/* Encabezado */}
                                            <Card sx={{ mb: 3 }}>
                                                <CardContent>
                                                    <Typography variant="subtitle1" gutterBottom color="primary">
                                                        Encabezado de Partida
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={4}>
                                                            <Autocomplete
                                                                options={sucursales}
                                                                getOptionLabel={(option) => option.nombreSucursal || ''}
                                                                value={filtroSucursal}
                                                                onChange={(e, newValue) => setFiltroSucursal(newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Sucursal" fullWidth />
                                                                )}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <Autocomplete
                                                                options={unidadesDisponibles}
                                                                getOptionLabel={(option) => option.unidadNegocio || ''}
                                                                value={filtroUnidadNegocio}
                                                                onChange={(e, newValue) => setFiltroUnidadNegocio(newValue)}
                                                                disabled={!filtroSucursal}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Unidad de Negocio" required fullWidth />
                                                                )}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Fecha de Partida"
                                                                type="date"
                                                                value={fechaPartida}
                                                                onChange={(e) => setFechaPartida(e.target.value)}
                                                                required
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <Autocomplete
                                                                options={estados}
                                                                getOptionLabel={(option) => option.estado || ''}
                                                                value={estado}
                                                                onChange={(e, newValue) => setEstado(newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Estado" fullWidth />
                                                                )}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <Autocomplete
                                                                options={tiposPartida}
                                                                getOptionLabel={(option) => option.descripcionTipoPartida || ''}
                                                                value={tipoPartida}
                                                                onChange={(e, newValue) => setTipoPartida(newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Tipo de Partida" fullWidth />
                                                                )}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={8}>
                                                            <TextField
                                                                fullWidth
                                                                label="Concepto"
                                                                value={concepto}
                                                                onChange={(e) => setConcepto(e.target.value)}
                                                                required
                                                                multiline
                                                                rows={2}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Observaciones"
                                                                value={observaciones}
                                                                onChange={(e) => setObservaciones(e.target.value)}
                                                                multiline
                                                                rows={2}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Divider sx={{ my: 2 }} />

                                            {/* Detalle de Asientos */}
                                            <Card sx={{ mb: 3 }}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                        <Typography variant="subtitle1" color="primary">
                                                            Detalle de Asientos Contables
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={agregarAsiento}
                                                        >
                                                            Agregar Asiento
                                                        </Button>
                                                    </Box>

                                                    <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                                    <TableCell align="center" sx={{ minWidth: 50, width: 50 }}>#</TableCell>
                                                                    <TableCell sx={{ minWidth: 350, width: 350 }}>Cuenta Contable</TableCell>
                                                                    <TableCell align="right" sx={{ minWidth: 120, width: 120 }}>Debe</TableCell>
                                                                    <TableCell align="right" sx={{ minWidth: 120, width: 120 }}>Haber</TableCell>
                                                                    <TableCell sx={{ minWidth: 200, width: 200 }}>Concepto</TableCell>
                                                                    <TableCell sx={{ minWidth: 300, width: 300 }}>Centro de Costo</TableCell>
                                                                    <TableCell align="center" sx={{ minWidth: 100, width: 100 }}>Acciones</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {asientos.length === 0 ? (
                                                                    <TableRow>
                                                                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                                            No hay asientos agregados. Haga clic en "Agregar Asiento" para comenzar.
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ) : (
                                                                    asientos.map((asiento, index) => {
                                                                        return (
                                                                            <TableRow key={index}>
                                                                                <TableCell align="center">{asiento.secuencia}</TableCell>
                                                                                <TableCell>
                                                                                    <Autocomplete
                                                                                        size="small"
                                                                                        options={cuentas}
                                                                                        getOptionLabel={(option) => 
                                                                                            `${option.codigo || ''} - ${option.descripcion || ''}`
                                                                                        }
                                                                                        value={asiento.cuenta}
                                                                                        onChange={(e, newValue) => 
                                                                                            actualizarAsiento(index, 'cuenta', newValue)
                                                                                        }
                                                                                        renderInput={(params) => (
                                                                                            <TextField 
                                                                                                {...params} 
                                                                                                placeholder="Seleccione cuenta"
                                                                                                multiline
                                                                                            />
                                                                                        )}
                                                                                        renderOption={(props, option) => (
                                                                                            <li {...props} style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                                                                <Box>
                                                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                                                        {option.codigo}
                                                                                                    </Typography>
                                                                                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                                                                        {option.descripcion}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            </li>
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        size="small"
                                                                                        type="text"
                                                                                        value={asiento.debeStr}
                                                                                        onChange={(e) => {
                                                                                            actualizarAsiento(index, 'debeStr', e.target.value);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const valor = parseFormattedNumber(e.target.value);
                                                                                            const redondeado = parseFloat((valor || 0).toFixed(2));
                                                                                            actualizarAsiento(index, 'debe', redondeado);
                                                                                            actualizarAsiento(index, 'debeStr', redondeado > 0 ? formatNumber(redondeado) : '');
                                                                                        }}
                                                                                        inputProps={{ 
                                                                                            style: { textAlign: 'right' },
                                                                                            inputMode: 'decimal',
                                                                                            pattern: '[0-9.,]*'
                                                                                        }}
                                                                                        disabled={asiento.haber > 0}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        size="small"
                                                                                        type="text"
                                                                                        value={asiento.haberStr}
                                                                                        onChange={(e) => {
                                                                                            actualizarAsiento(index, 'haberStr', e.target.value);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const valor = parseFormattedNumber(e.target.value);
                                                                                            const redondeado = parseFloat((valor || 0).toFixed(2));
                                                                                            actualizarAsiento(index, 'haber', redondeado);
                                                                                            actualizarAsiento(index, 'haberStr', redondeado > 0 ? formatNumber(redondeado) : '');
                                                                                        }}
                                                                                        inputProps={{ 
                                                                                            style: { textAlign: 'right' },
                                                                                            inputMode: 'decimal',
                                                                                            pattern: '[0-9.,]*'
                                                                                        }}
                                                                                        disabled={asiento.debe > 0}
                                                                                        placeholder="0.00"
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        size="small"
                                                                                        value={asiento.concepto || ''}
                                                                                        onChange={(e) => 
                                                                                            actualizarAsiento(index, 'concepto', e.target.value)
                                                                                        }
                                                                                        placeholder="Concepto específico"
                                                                                        multiline
                                                                                        rows={2}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Autocomplete
                                                                                        size="small"
                                                                                        options={getCentrosCostoFiltrados()}
                                                                                        getOptionLabel={(option) => {
                                                                                            if (!option) return '';
                                                                                            const unidad = unidadesNegocio.find(u => u.id === option.idUnidadNegocio)?.unidadNegocio || 'N/A';
                                                                                            const sucursal = sucursales.find(s => s.idSucursal === option.idSucursal)?.nombreSucursal || 'N/A';
                                                                                            const proceso = procesos.find(p => p.id === option.idProceso)?.descripcion || 'N/A';
                                                                                            return `${unidad} - ${sucursal} - ${proceso}`;
                                                                                        }}
                                                                                        value={asiento.centroCosto}
                                                                                        onChange={(e, newValue) => 
                                                                                            actualizarAsiento(index, 'centroCosto', newValue)
                                                                                        }
                                                                                        renderInput={(params) => (
                                                                                            <TextField {...params} placeholder="Centro de costo" />
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="error"
                                                                                        onClick={() => eliminarAsiento(index)}
                                                                                    >
                                                                                        <DeleteIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>

                                                    {/* Totales */}
                                                    {asientos.length > 0 && (
                                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                                                            <Chip 
                                                                label={`Total Debe: L ${formatNumber(totalDebe)}`}
                                                                color="info"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                            <Chip 
                                                                label={`Total Haber: L ${formatNumber(totalHaber)}`}
                                                                color="info"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                            {cuadra ? (
                                                                <Chip 
                                                                    icon={<CheckCircleIcon />}
                                                                    label="¡Cuadra!"
                                                                    color="success"
                                                                    sx={{ fontWeight: 'bold' }}
                                                                />
                                                            ) : (
                                                                <Chip 
                                                                    icon={<WarningIcon />}
                                                                    label={`Diferencia: L ${formatNumber(diferencia)}`}
                                                                    color="error"
                                                                    sx={{ fontWeight: 'bold' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Botones de acción */}
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    size="large"
                                                    startIcon={<SaveIcon />}
                                                    disabled={!tienePermiso('Editar Partida Contable')}
                                                >
                                                    {editingPartida ? 'Actualizar Partida' : 'Guardar Partida'}
                                                </Button>
                                                {editingPartida && (
                                                    <Button
                                                        variant="outlined"
                                                        size="large"
                                                        startIcon={<CancelIcon />}
                                                        onClick={limpiarFormulario}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                )}
                                            </Box>
                                        </form>
                                    </Paper>

                                    <div className="ln_solid" />

                                    {/* Filtros de búsqueda y paginación */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="h6" gutterBottom>Partidas Contables Registradas ({totalPartidas})</Typography>
                                    </Box>
                                    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Fecha Inicio"
                                                    type="date"
                                                    value={fechaInicio}
                                                    onChange={(e) => setFechaInicio(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Fecha Fin"
                                                    type="date"
                                                    value={fechaFin}
                                                    onChange={(e) => setFechaFin(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Autocomplete
                                                    size="small"
                                                    options={estados}
                                                    getOptionLabel={(option) => option.estado || ''}
                                                    value={filtroEstado}
                                                    onChange={(e, newValue) => setFiltroEstado(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Estado" 
                                                            placeholder="Todos"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Autocomplete
                                                    size="small"
                                                    options={tiposPartida}
                                                    getOptionLabel={(option) => option.descripcionTipoPartida || ''}
                                                    value={filtroTipoPartida}
                                                    onChange={(e, newValue) => setFiltroTipoPartida(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Tipo Partida" 
                                                            placeholder="Todos"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Buscar"
                                                    placeholder="Número, concepto, referencia..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleBuscar}
                                                >
                                                    BUSCAR
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell>Código Tipo Partida</TableCell>
                                                    <TableCell>Fecha</TableCell>
                                                    <TableCell>Concepto</TableCell>
                                                    <TableCell align="right">Total Debe</TableCell>
                                                    <TableCell align="right">Total Haber</TableCell>
                                                    <TableCell>Estado</TableCell>
                                                    <TableCell align="center">Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {partidas.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                            No hay partidas registradas
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    partidas.map((partida) => (
                                                        <TableRow key={partida.idPartidaContable} hover>
                                                            <TableCell>{partida.codigoTipoPartidaCorrelativo || 'N/A'}</TableCell>
                                                            <TableCell>{partida.fechaPartida}</TableCell>
                                                            <TableCell>{partida.concepto}</TableCell>
                                                            <TableCell align="right">
                                                                L {formatNumber(partida.totalDebe || 0)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                L {formatNumber(partida.totalHaber || 0)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={estados.find(e => e.id === partida.idEstado)?.estado || 'N/A'}
                                                                    size="small"
                                                                    color={partida.idEstado === 1 ? 'success' : 'default'}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => cargarPartidaParaEditar(partida)}
                                                                    title="Editar"
                                                                    disabled={!tienePermiso('Editar Partida Contable')}
                                                                >
                                                                    <SaveIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="secondary"
                                                                    onClick={() => imprimirPartida(partida.idPartidaContable)}
                                                                    title="Imprimir PDF"
                                                                >
                                                                    <PrintIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setPartidaToDelete(partida);
                                                                        setOpenDialog(true);
                                                                    }}
                                                                    title="Eliminar"
                                                                    disabled={!tienePermiso('Eliminar Partida Contable')}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* Controles de paginación */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Mostrando {partidas.length} de {totalPartidas} registros | Página {page} de {totalPages}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={page <= 1}
                                                onClick={() => handlePageChange(1)}
                                            >
                                                Primera
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={page <= 1}
                                                onClick={() => handlePageChange(page - 1)}
                                            >
                                                Anterior
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={page >= totalPages}
                                                onClick={() => handlePageChange(page + 1)}
                                            >
                                                Siguiente
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={page >= totalPages}
                                                onClick={() => handlePageChange(totalPages)}
                                            >
                                                Última
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Dialog de confirmación de eliminación */}
                                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                                        <DialogTitle>Confirmar eliminación</DialogTitle>
                                        <DialogContent>
                                            <DialogContentText>
                                                ¿Está seguro de eliminar la partida "{partidaToDelete?.numeroPartida}"?
                                                Esta acción eliminará también todos los asientos asociados y no se puede deshacer.
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setOpenDialog(false)} color="primary">
                                                Cancelar
                                            </Button>
                                            <Button onClick={handleDelete} color="error" autoFocus disabled={!tienePermiso('Eliminar Partida Contable')}>
                                                Eliminar
                                            </Button>
                                        </DialogActions>
                                    </Dialog>

                                    {/* Modal para visualizar PDF */}
                                    <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: '90%',
                                                height: '90vh',
                                                bgcolor: 'white',
                                                boxShadow: 24,
                                                borderRadius: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {/* Header del modal */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 2,
                                                    borderBottom: '1px solid #ddd',
                                                    bgcolor: '#f5f5f5'
                                                }}
                                            >
                                                <Typography variant="h6" component="h2">
                                                    {pdfNombre}
                                                </Typography>
                                                <Box>
                                                    <IconButton
                                                        onClick={handleDownloadPdf}
                                                        color="primary"
                                                        title="Descargar PDF"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={handleCloseModalPdf}
                                                        color="error"
                                                        title="Cerrar"
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {/* Contenedor del PDF */}
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    bgcolor: '#525659'
                                                }}
                                            >
                                                {pdfUrl && (
                                                    <iframe
                                                        src={pdfUrl}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            border: 'none'
                                                        }}
                                                        title="Visor de PDF"
                                                    />
                                                )}
                                            </Box>

                                            {/* Footer del modal */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    p: 2,
                                                    borderTop: '1px solid #ddd',
                                                    bgcolor: '#f5f5f5'
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={handleCloseModalPdf}
                                                    startIcon={<CloseIcon />}
                                                >
                                                    Cerrar
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>
        </div>
    );
}

export default GestionPartidaContable;
